import { SubmitHandler, useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router';
import { useMutation, useQuery } from '@apollo/client';
import { Flex, Stack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Typography } from '@shinetools/sunshine-universal';

import { UpdateShineRecurringAccountingExportSettingsInput } from '__generated__/GQL';
import Button from 'components/_core/Button';
import BackButton from 'components/BackButton';
import useToast from 'components/ToastProvider/useToast';
import { AccountingExportForm } from 'features/Accounting/components/AccountingExportForm';
import {
  accountingExportSchema,
  ShineRecurringAccountingExportSchema,
} from 'features/Accounting/components/AccountingExportForm/accountingExport.schema';
import { formatDate } from 'helpers/date';
import * as Layout from 'layouts/ContentLayout';

import { GetShineRecurringAccountingExportDocument } from './graphql/getShineRecurringAccountingExport.gql';
import { UpdateShineRecurringAccountingExportDocument } from './graphql/updateShineRecurringAccountingExport.gql';
import locales from './locales';

interface RouteState {
  exportId: string;
}

export const EditRecurringExport = () => {
  const { exportId: accountingExportSettingsId } = useParams<RouteState>();
  const { showToast } = useToast();
  const history = useHistory();

  const form = useForm<ShineRecurringAccountingExportSchema>({
    defaultValues: {
      recipients: [],
    },
    resolver: zodResolver(accountingExportSchema),
  });

  const [
    updateShineRecurringAccountingExport,
    { loading: updateRecurringAccountingExportLoading },
  ] = useMutation(UpdateShineRecurringAccountingExportDocument);

  const { data, loading: getAccountingExportLoading } = useQuery(
    GetShineRecurringAccountingExportDocument,
    {
      onCompleted: ({ shineRecurringAccountingExportSetting }) => {
        form.setValue('cadence', shineRecurringAccountingExportSetting.cadence);
        form.setValue(
          'cadenceOffset',
          shineRecurringAccountingExportSetting.cadenceOffset,
        );
        form.setValue(
          'formats',
          shineRecurringAccountingExportSetting.settings.formats,
        );
        form.setValue('type', shineRecurringAccountingExportSetting.type);
        form.setValue(
          'includeInvoices',
          shineRecurringAccountingExportSetting.settings.includeInvoices,
        );
        form.setValue(
          'includePersonalTransactions',
          shineRecurringAccountingExportSetting.settings
            .includePersonalTransactions,
        );
        form.setValue(
          'includeBankStatements',
          shineRecurringAccountingExportSetting.settings.includeBankStatements,
        );
      },
      variables: { accountingExportSettingsId },
    },
  );

  const onSubmit: SubmitHandler<ShineRecurringAccountingExportSchema> = async (
    formData,
  ) => {
    const { cadence, cadenceOffset, type, ...rest } = formData;

    const input = {
      accountingExportSettingsId,
      cadenceSettings: { offset: cadenceOffset, period: cadence },
      ...rest,
    } satisfies UpdateShineRecurringAccountingExportSettingsInput;

    await updateShineRecurringAccountingExport({
      onCompleted: () => {
        showToast({ message: locales.successMessage, type: 'success' });
      },
      onError: () =>
        showToast({ message: locales.errorMessage, type: 'error' }),
      variables: { input },
    });
  };

  const nextExportAtText = locales.formatString(locales.nextExportAt, {
    date: data
      ? formatDate(
          new Date(data.shineRecurringAccountingExportSetting.nextExportAt),
          'PPP',
        )
      : '',
  });

  return (
    <Layout.Root>
      <Layout.Body>
        <BackButton />

        <Typography.Header marginTop="$space.32" size="large">
          {locales.title}
        </Typography.Header>

        {!data && getAccountingExportLoading ? (
          /**
           * @todo Add proper loading state to edit page
           * @see https://linear.app/shine/issue/CAC-1057
           */
          <Typography.Text>Loading</Typography.Text>
        ) : (
          <Stack gap="space-32" marginTop="space-8">
            <Typography.Text>{nextExportAtText}</Typography.Text>

            <AccountingExportForm form={form} isEdit />

            <Flex justifyContent="space-between">
              <Button onClick={() => history.goBack()} variant="secondary">
                {locales.cancel}
              </Button>

              <Button
                icon="tick"
                iconPosition="left"
                isLoading={updateRecurringAccountingExportLoading}
                onClick={form.handleSubmit(onSubmit)}
              >
                {locales.confirm}
              </Button>
            </Flex>
          </Stack>
        )}
      </Layout.Body>
    </Layout.Root>
  );
};
